import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MarkdownRenderer';
import SecureImage from '../components/SecureImage';
import { useProgressContext } from '../components/ModulePageLayout';
import { useDiagnosticAccessGuard } from '../hooks/useDiagnosticAccessGuard';
import QuestionModule from '../components/QuestionModule';
import VideoPlayer from '../components/VideoPlayer';
import { useModuleTimeTracker } from '../hooks/useModuleTimeTracker';
import { fetchStudentCourse, notesAPI, progressAPI, reminderPreferencesAPI, submissionAPI } from '../services/api';
import { getLocalizedText } from '../utils/i18n';
import { buildModuleFileDownloadName, downloadResourceFromUrl, isRestrictedDownloadEnvironment } from '../utils/download';
import { normalizeId } from '../utils/id';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import {
  BookmarkIcon,
  CheckCircleIcon as CheckIcon,
  DescriptionIcon,
  DownloadIcon,
  InfoIcon,
  LinkIcon,
  OpenInNewIcon,
  PlayIcon,
  ThumbUpIcon,
  TimeIcon,
  WarningTriangleIcon
} from '../utils/icons';
import { getNextItem, getNextItemPath } from '../utils/navigation';
import EmailResourceButton from '../components/EmailResourceButton';

// Note: Lightbulb icon not available in Feather, using InfoIcon as replacement
const LightbulbIcon = InfoIcon;

interface Module {
  type: 'title' | 'richtext' | 'video' | 'audio' | 'file' | 'question' | 'citation' | 'link' | 'notification' | 'image';
  order: number;
  content: any;
}

interface Capsule {
  title: string | { [key: string]: string };
  description?: string | { [key: string]: string };
  modules: Module[];
  order: number;
  duration?: number;
  image?: string;
  isCompleted?: boolean;
  estimatedDuration?: number;
}

const buildCitationAuthorImageUrl = (imagePath: string): string => {
  if (!imagePath) return '';
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  const baseUrl = API_BASE_URL.replace('/api', '');
  if (imagePath.startsWith('/')) {
    if (imagePath.startsWith('/uploads/')) {
      return `${API_BASE_URL}${imagePath}`;
    }
    return `${baseUrl}${imagePath}`;
  }
  return `${API_BASE_URL}/uploads/${imagePath}`;
};

/** Défini au niveau module pour que React ne démonte pas le bloc à chaque re-render parent (ex. saisie question). */
const CitationModule: React.FC<{ module: Module }> = ({ module }) => {
  const { t } = useTranslation();
  const citationContent = module.content?.content || module.content?.text || module.content;
  const authorName = module.content?.name ? getLocalizedText(module.content.name) : '';
  const authorDescription = module.content?.description ? getLocalizedText(module.content.description) : '';
  const authorImage = module.content?.image || module.content?.avatar || null;
  const [authorImageBlobUrl, setAuthorImageBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!authorImage) {
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setAuthorImageBlobUrl(null);
      return;
    }

    let cancelled = false;

    const revokeBlob = () => {
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
      blobUrlRef.current = null;
    };

    const loadImage = async () => {
      revokeBlob();
      setAuthorImageBlobUrl(null);

      const tryDirect = (): Promise<boolean> =>
        new Promise((resolve) => {
          const imageUrl = buildCitationAuthorImageUrl(authorImage);
          const img = new Image();
          img.onload = () => {
            if (!cancelled) {
              setAuthorImageBlobUrl(imageUrl);
            }
            resolve(true);
          };
          img.onerror = () => resolve(false);
          img.src = imageUrl;
        });

      const directOk = await tryDirect();
      if (cancelled) return;
      if (directOk) return;

      try {
        const token = localStorage.getItem('student_token');
        const imageUrlWithAuth = authorImage.startsWith('http')
          ? authorImage
          : buildCitationAuthorImageUrl(authorImage);

        const response = await fetch(imageUrlWithAuth, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (cancelled) return;

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          blobUrlRef.current = url;
          setAuthorImageBlobUrl(url);
        } else {
          console.error('❌ Failed to load citation author image:', response.status);
          setAuthorImageBlobUrl(null);
        }
      } catch (error) {
        console.error('❌ Error loading citation author image:', error);
        if (!cancelled) setAuthorImageBlobUrl(null);
      }
    };

    void loadImage();

    return () => {
      cancelled = true;
      if (blobUrlRef.current?.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [authorImage]);

  return (
    <div className="capsule__module-citation">
      <div className="capsule__citation">
        <div className="capsule__citation-quote">
          <div className="capsule__citation-text">
            <MarkdownRenderer
              content={`"${getLocalizedText(citationContent)}"`}
            />
          </div>
        </div>
        {(authorName || authorDescription || authorImage) && (
          <div className="capsule__citation-author">
            {authorImage && authorImageBlobUrl && (
              <img
                src={authorImageBlobUrl}
                alt={authorName || t('capsule.author')}
                className="capsule__citation-avatar"
                onError={(e) => {
                  console.error('❌ Failed to load citation author image:', authorImage);
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            )}
            <div className="capsule__citation-author-info">
              {authorName && (
                <p className="capsule__citation-author-name">{authorName.toUpperCase()}</p>
              )}
              {authorDescription && (
                <p className="capsule__citation-author-description">{authorDescription}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Capsule: React.FC = () => {
  const { courseId, chapterId, capsuleId: capsuleIdParam, capsuleOrder } = useParams<{
    courseId: string;
    chapterId: string;
    capsuleId?: string;
    capsuleOrder?: string;
  }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refreshProgress, refreshAllProgress, refreshCourse } = useProgressContext();
  useDiagnosticAccessGuard();

  const [capsule, setCapsule] = useState<Capsule | null>(null);
  const [capsuleId, setCapsuleId] = useState<string | null>(null); // Store capsule ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [questionAnswers, setQuestionAnswers] = useState<{ [key: string]: string }>({}); // key is moduleId
  const [questionSubmissionDates, setQuestionSubmissionDates] = useState<{ [key: string]: Date | string }>({}); // key is moduleId
  const [notes, setNotes] = useState<string>('');
  const [course, setCourse] = useState<any>(null);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null); // State for image blob URL
  const [knownEmail, setKnownEmail] = useState<string | null>(null);
  const downloadLocksRef = useRef<Map<string, number>>(new Map());
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(() => new Set());

  // Use ref to prevent duplicate calls
  const loadingRef = React.useRef(false);
  // Ref for auto-save timeout
  const notesSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Latest typed content for flush-on-leave (ids come from effect closures)
  const notesContentRef = useRef(notes);
  notesContentRef.current = notes;

  useEffect(() => {
    let cancelled = false;
    reminderPreferencesAPI
      .getGlobal()
      .then((prefs) => {
        if (!cancelled) {
          setKnownEmail(prefs.email);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setKnownEmail(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Track time spent on this capsule
  useModuleTimeTracker({
    courseId: courseId || '',
    chapterId: chapterId || '',
    moduleType: 'capsule',
    moduleId: capsuleId || '',
    enabled: !!courseId && !!chapterId && !!capsuleId && !loading
  });

  useEffect(() => {
    if (courseId && chapterId && (capsuleIdParam || capsuleOrder) && !loadingRef.current) {
      loadingRef.current = true;
      loadCapsuleData().finally(() => {
        loadingRef.current = false;
      });
    }
  }, [courseId, chapterId, capsuleIdParam, capsuleOrder]);

  // Auto-save notes with debounce (1.5s after typing stops).
  // Cleanup MUST only cancel the timer — never POST (that caused 1 request per keystroke).
  useEffect(() => {
    if (!notes.trim() || !courseId || !chapterId || !capsuleId) {
      return;
    }

    notesSaveTimeoutRef.current = setTimeout(() => {
      notesSaveTimeoutRef.current = null;
      void notesAPI.saveNote(courseId, chapterId, capsuleId, notes).catch((error: unknown) => {
        console.error('Error saving notes:', error);
      });
    }, 1500);

    return () => {
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
    };
  }, [notes, courseId, chapterId, capsuleId]);

  // Flush latest notes when leaving the capsule / page (not on each keystroke).
  useEffect(() => {
    if (!courseId || !chapterId || !capsuleId) {
      return;
    }

    const flush = () => {
      const content = notesContentRef.current;
      if (!content.trim()) return;
      void notesAPI.saveNote(courseId, chapterId, capsuleId, content).catch((error: unknown) => {
        console.error('Error saving notes on leave:', error);
      });
    };

    const onPageHide = () => flush();
    window.addEventListener('pagehide', onPageHide);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      if (notesSaveTimeoutRef.current) {
        clearTimeout(notesSaveTimeoutRef.current);
        notesSaveTimeoutRef.current = null;
      }
      flush();
    };
  }, [courseId, chapterId, capsuleId]);

  // Build image URL if capsule has an image
  const buildImageUrl = (imagePath: string): string => {
    if (!imagePath || imagePath.trim() === '') return '';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    const baseUrl = apiUrl.replace('/api', '');
    if (imagePath.startsWith('/')) {
      if (imagePath.startsWith('/uploads/')) {
        return `${apiUrl}${imagePath}`;
      }
      return `${baseUrl}${imagePath}`;
    }
    return `${apiUrl}/uploads/${imagePath}`;
  };

  // Load image with authentication (same logic as Workshop)
  // This hook must be called before any conditional returns
  React.useEffect(() => {
    const capsuleImage = capsule?.image;
    if (capsuleImage) {
      const loadImage = async () => {
        try {
          const token = localStorage.getItem('student_token');
          const imageUrl = capsuleImage.startsWith('http')
            ? capsuleImage
            : buildImageUrl(capsuleImage);

          const response = await fetch(imageUrl, {
            headers: token ? {
              'Authorization': `Bearer ${token}`
            } : {}
          });

          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setImageBlobUrl(url);
          } else {
            setImageBlobUrl(null);
          }
        } catch (error) {
          console.error('❌ Error loading image:', error);
          setImageBlobUrl(null);
        }
      };

      loadImage();

      return () => {
        setImageBlobUrl((currentBlobUrl) => {
          if (currentBlobUrl) {
            window.URL.revokeObjectURL(currentBlobUrl);
          }
          return null;
        });
      };
    } else {
      setImageBlobUrl(null);
    }
  }, [capsule?.image]);

  const loadCapsuleData = async () => {
    if (!courseId || !chapterId || (!capsuleIdParam && !capsuleOrder)) {
      setError(t('common.missingParameters'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Load course data (ModulePageLayout will cache it, but we still need it here)
      const courseData = await fetchStudentCourse(courseId, { full: true });
      setCourse(courseData);

      // Find the capsule in the chapter
      const chapter = courseData.chapters?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId));
      if (chapter) {
        let capsuleItem: any = null;
        if (capsuleIdParam) {
          capsuleItem = chapter.items?.find(
            (item: any) => item.itemType === 'capsule' && normalizeId(item?._id || item?.id) === normalizeId(capsuleIdParam)
          );
          // Legacy compatibility: old URLs used order in place of id
          if (!capsuleItem && /^\d+$/.test(capsuleIdParam)) {
            const legacyOrder = parseInt(capsuleIdParam, 10);
            capsuleItem = chapter.items?.find(
              (item: any) => item.itemType === 'capsule' && item.order === legacyOrder
            );
          }
        } else if (capsuleOrder) {
          const capsuleOrderNum = parseInt(capsuleOrder);
          capsuleItem = chapter.items?.find(
            (item: any) => item.itemType === 'capsule' && item.order === capsuleOrderNum
          );
        }
        if (capsuleItem) {
          // Store capsule ID
          if (normalizeId(capsuleItem._id)) {
            setCapsuleId(normalizeId(capsuleItem._id));
          }

          setCapsule(capsuleItem);

          // If we arrived via legacy order URL, redirect to ID-based URL
          if (!capsuleIdParam && capsuleOrder && normalizeId(capsuleItem._id)) {
            navigate(
              `/course/${courseId}/chapter/${chapterId}/capsule/${normalizeId(capsuleItem._id)}`,
              { replace: true }
            );
          }

          // Load existing note if any (using capsuleId)
          if (normalizeId(capsuleItem._id)) {
            try {
              const capsuleIdStr = normalizeId(capsuleItem._id);
              const existingNote = await notesAPI.getNote(courseId, chapterId, capsuleIdStr);
              if (existingNote && existingNote.content) {
                setNotes(existingNote.content);
              } else {
                // Note exists but has no content, clear the field
                setNotes('');
              }
            } catch (error: any) {
              // No existing note (404 or other error), clear the field
              console.log('No existing note found for capsule:', capsuleItem._id, error?.response?.status);
              setNotes('');
            }
          } else {
            // No capsule ID, clear notes
            setNotes('');
          }

          // Load existing submissions for question modules using capsule ID
          if (normalizeId(capsuleItem._id)) {
            try {
              const capsuleIdStr = normalizeId(capsuleItem._id);
              const submissions = await submissionAPI.getCapsuleSubmissions(courseId, chapterId, capsuleIdStr);
              const submissionsMap: { [key: string]: string } = {};
              const submissionDatesMap: { [key: string]: Date | string } = {};
              if (Array.isArray(submissions)) {
                submissions.forEach((sub: any) => {
                  // moduleId might be an ObjectId, convert to string
                  const moduleIdStr = sub.moduleId?.toString() || sub.moduleId;
                  submissionsMap[moduleIdStr] = sub.answer;
                  // Store submission date if available
                  if (sub.submissionDate) {
                    submissionDatesMap[moduleIdStr] = sub.submissionDate;
                  } else if (sub.createdAt) {
                    submissionDatesMap[moduleIdStr] = sub.createdAt;
                  }
                });
              }
              setQuestionAnswers(submissionsMap);
              setQuestionSubmissionDates(submissionDatesMap);
            } catch (error: any) {
              // No submissions found, that's fine
            }
          }
        } else {
          setError(t('capsule.notFound'));
        }
      } else {
        setError(t('capsule.chapterNotFound'));
      }
    } catch (err: any) {
      console.error('Error loading capsule:', err);
      setError(getLearnerFacingApiError(err, t));
    } finally {
      setLoading(false);
    }
  };



  const handleQuestionSave = async (moduleId: string, answer: string) => {
    if (!courseId || !chapterId || !capsuleId || !moduleId) {
      return;
    }

    try {
      const response = await submissionAPI.saveSubmission(courseId, chapterId, capsuleId, moduleId, answer);

      // Update local state
      setQuestionAnswers(prev => ({
        ...prev,
        [moduleId]: answer
      }));

      // Update submission date
      const submissionDate = response?.submission?.submissionDate || response?.submission?.createdAt || new Date();
      setQuestionSubmissionDates(prev => ({
        ...prev,
        [moduleId]: submissionDate
      }));

    } catch (error: any) {
      console.error('❌ Error saving submission:', error);
      setError(error.response?.data?.error || t('capsule.saveError'));
    }
  };

  const handleDownloadResource = (module: Module, resourceKey: string) => {
    if (!resourceKey) return;

    const now = Date.now();
    const lockedUntil = downloadLocksRef.current.get(resourceKey) || 0;
    if (now < lockedUntil) return;

    const url = getLocalizedText(module.content?.fileUrl || module.content?.url);
    if (!url) return;

    const lockMs = 1500;
    downloadLocksRef.current.set(resourceKey, now + lockMs);

    const fileName = buildModuleFileDownloadName(module.content, t('capsule.file'));
    void downloadResourceFromUrl(url, fileName).catch((downloadError: unknown) => {
      console.error('Error downloading file:', downloadError);
      setError(t('capsule.downloadError'));
    });

    window.setTimeout(() => {
      setDownloadingIds((prev) => new Set(prev).add(resourceKey));
    }, 0);

    window.setTimeout(() => {
      downloadLocksRef.current.delete(resourceKey);
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(resourceKey);
        return next;
      });
    }, lockMs);
  };

  const renderModule = (module: Module, index: number) => {
    switch (module.type) {
      case 'title':
        return (
          <div
            key={index}
            className="capsule__module-title"
          >
            <span className="capsule__module-title-text">{getLocalizedText(module.content?.title || module.content)}</span>
          </div>
        );

      case 'richtext':
        return (
          <div key={index} className="capsule__module-richtext">
            <MarkdownRenderer
              content={getLocalizedText(module.content?.text || module.content)}
            />
          </div>
        );

      case 'video':
        const videoUrl = getLocalizedText(module.content?.fileUrl || module.content?.url || module.content);
        const videoThumbnail = module.content?.thumbnail;

        console.log('📹 Video module data:', {
          moduleContent: module.content,
          videoUrl,
          videoThumbnail,
          fileUrl: module.content?.fileUrl,
          url: module.content?.url
        });

        return (
          <div key={index} className="capsule__module-video">
            <VideoPlayer
              url={videoUrl}
              thumbnail={videoThumbnail}
              getLocalizedText={getLocalizedText}
            />
          </div>
        );

      case 'audio':
        return (
          <Box key={index} sx={{ mb: 3 }}>
            <Card sx={{ borderRadius: 2, backgroundColor: '#fff' }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    sx={{
                      backgroundColor: 'var(--brand-primary)',
                      color: 'var(--brand-on-primary)',
                      width: 56,
                      height: 56,
                      '&:hover': {
                        backgroundColor: 'var(--brand-primary-hover)'
                      }
                    }}
                    onClick={() => {
                      // TODO: Implement audio playback
                    }}
                  >
                    <PlayIcon />
                  </IconButton>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
                      {getLocalizedText(module.content?.title || t('capsule.audio'))}
                    </Typography>
                    <Box
                      sx={{
                        height: 4,
                        backgroundColor: '#e0e0e0',
                        borderRadius: 2,
                        mt: 1,
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        sx={{
                          width: '30%',
                          height: '100%',
                          backgroundColor: 'var(--brand-primary)',
                          borderRadius: 2
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        );

      case 'file': {
        const fileModuleId = normalizeId((module as any)._id);
        const downloadKey = fileModuleId || `file-${index}`;
        const isDownloading = downloadingIds.has(downloadKey);
        return (
          <div key={index} className="capsule__module-file-container">
            <div className="capsule__module-file">
              <div className="capsule__module-file-icon">
                <DescriptionIcon size={24} color="#fff" />
              </div>
              <div className="capsule__module-file-content">
                <Typography className="capsule__module-file-title">
                  {getLocalizedText(module.content?.title || module.content?.fileName || t('capsule.file'))}
                </Typography>
                <Typography className="capsule__module-file-type">
                  {getLocalizedText(module.content?.fileType || t('capsule.pdf'))}
                </Typography>
              </div>
              <div className="capsule__module-file-actions">
                <Tooltip title={t('capsule.downloadResourceTooltip')}>
                  <span>
                    <IconButton
                      className="capsule__module-file-download"
                      onClick={() => void handleDownloadResource(module, downloadKey)}
                      disabled={isDownloading}
                      aria-label={t('capsule.downloadResourceTooltip')}
                    >
                      {isDownloading ? (
                        <CircularProgress size={18} sx={{ color: '#fff' }} />
                      ) : (
                        <DownloadIcon size={20} color="#fff" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {courseId && chapterId && capsuleId && fileModuleId && (
                  <EmailResourceButton
                    courseId={courseId}
                    chapterId={chapterId}
                    itemId={capsuleId}
                    moduleId={fileModuleId}
                    knownEmail={knownEmail}
                    onEmailSaved={setKnownEmail}
                  />
                )}
              </div>
            </div>
            {isRestrictedDownloadEnvironment() && (
              <p className="capsule__module-file-hint">
                {t('capsule.downloadMayBeBlocked')}
              </p>
            )}
          </div>
        );
      }

      case 'link':
        return (
          <div key={index} className="capsule__module-file-container">
            <div className="capsule__module-file">
              <div className="capsule__module-file-icon">
                <LinkIcon size={24} color="#fff" />
              </div>
              <div className="capsule__module-file-content">
                <Typography className="capsule__module-file-title">
                  {getLocalizedText(module.content?.title || t('capsule.link'))}
                </Typography>
                <Typography className="capsule__module-file-type">
                  {getLocalizedText(module.content?.url || '')}
                </Typography>
              </div>
              <IconButton
                className="capsule__module-file-download"
                onClick={() => {
                  const url = getLocalizedText(module.content?.url);
                  if (url) {
                    window.open(url, '_blank');
                  }
                }}
              >
                <OpenInNewIcon size={20} color="#fff" />
              </IconButton>
            </div>
          </div>
        );

      case 'citation':
        return (
          <CitationModule
            key={normalizeId((module as any)._id) || `citation-${index}`}
            module={module}
          />
        );

      case 'notification':
        const notificationType = module.content?.notificationType || 'info';

        // Get the text content
        const notificationText =
          getLocalizedText(module.content?.content).trim() ||
          getLocalizedText(module.content?.text).trim();
        const hasItems = notificationType === 'takeaway' && module.content?.items && Array.isArray(module.content.items) && module.content.items.length > 0;

        // Determine notification type and styles
        let notificationClass = 'capsule__notification--info';
        let icon = <InfoIcon size={20} />;
        let title = t('capsule.notificationTypes.info');

        if (notificationType === 'takeaway') {
          notificationClass = 'capsule__notification--takeaway';
          icon = <BookmarkIcon size={20} />;
          title = t('capsule.notificationTypes.takeaway');
        } else if (notificationType === 'attention') {
          notificationClass = 'capsule__notification--attention';
          icon = <WarningTriangleIcon size={20} />;
          title = t('capsule.notificationTypes.attention');
        } else if (notificationType === 'best-practice') {
          notificationClass = 'capsule__notification--best-practice';
          icon = <ThumbUpIcon size={20} />;
          title = t('capsule.notificationTypes.bestPractice');
        }

        return (
          <div key={index} className="capsule__module-notification">
            <div className={`capsule__notification ${notificationClass}`}>
              <div className="capsule__notification-header">
                <div className="capsule__notification-icon">
                  {icon}
                </div>
                <p className="capsule__notification-title">{title}</p>
              </div>
              <div className="capsule__notification-content">
                {hasItems ? (
                  <ul className="capsule__notification-list">
                    {module.content.items.map((item: any, idx: number) => (
                      <li key={idx}>
                        <MarkdownRenderer
                          content={getLocalizedText(item)}
                        />
                      </li>
                    ))}
                  </ul>
                ) : notificationText && notificationText.trim() ? (
                  <MarkdownRenderer
                    content={notificationText}
                  />
                ) : (
                  <p className="capsule__notification-empty">{t('capsule.noContent')}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 'image': {
        const raw = module.content?.imageByLang || module.content?.image || module.content;
        const imagePath = typeof raw === 'string' ? raw : getLocalizedText(raw || {});
        if (!imagePath) {
          return null;
        }

        return (
          <div key={index} className="capsule__module-image">
            <SecureImage srcPath={imagePath} borderRadius={4} />
          </div>
        );
      }

      case 'question':
        const questionType = module.content?.questionType || 'text';
        const moduleId = normalizeId((module as any)._id) || null;
        const savedAnswer = moduleId ? questionAnswers[moduleId] : undefined;

        if (questionType !== 'text') {
          return (
            <Card key={index} sx={{ mb: 3, borderRadius: 2, backgroundColor: '#fff' }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                  {t('capsule.questionTypeNotSupported', { type: questionType })}
                </Typography>
              </CardContent>
            </Card>
          );
        }

        if (!moduleId) {
          console.error('Module does not have an _id');
          return null;
        }

        const submissionDate = moduleId ? questionSubmissionDates[moduleId] : undefined;

        return (
          <QuestionModule
            key={index}
            module={module}
            savedAnswer={savedAnswer}
            submissionDate={submissionDate}
            onSave={(answer) => handleQuestionSave(moduleId, answer)}
            getLocalizedText={getLocalizedText}
          />
        );

      default:
        return null;
    }
  };

  // Show loading state only in the content area, not full screen
  if (loading) {
      return (
      <Box className="capsule__loading">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error || !capsule) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '400px', p: 3 }}>
        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2 }}>
          {error || t('capsule.notFound')}
        </Typography>
      </Box>
    );
  }

  const sortedModules = [...(capsule.modules || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  // Get capsule completion status from course data
  const capsuleItem = course?.chapters
    ?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId))
    ?.items?.find((item: any) => {
      const id = item?._id?.toString?.() || item?._id;
      if (capsuleId) return item.itemType === 'capsule' && id === capsuleId;
      return item.itemType === 'capsule' && item.order === parseInt(capsuleOrder || '0');
    });
  const isCompleted = capsuleItem?.isCompleted || false;

  // En admin le champ « Label » est persisté dans description (multilingue).
  const capsuleBannerLabel =
    getLocalizedText(capsule.description).trim() || t('capsule.label');

  // Calculate duration (use estimatedDuration or duration, but don't use default value)
  const duration = capsule.estimatedDuration ?? capsule.duration ?? undefined;

  return (
    <Box className="capsule">
      {/* Banner Header with Image */}
      {capsule.image ? (
        <Box className="capsule__banner">
          <img
            src={imageBlobUrl || buildImageUrl(capsule.image)}
            alt={getLocalizedText(capsule.title)}
            className="capsule__banner-image"
          />
          <Box className="capsule__banner-overlay">
            <Box className="capsule__banner-top">
              <Typography className="capsule__banner-label">
                {capsuleBannerLabel}
              </Typography>
              <Box className="capsule__banner-tags">
                {duration !== undefined && duration > 0 && (
                  <Box className="capsule__banner-tag">
                    <TimeIcon size={16} className="capsule__banner-tag-icon" />
                    <span>{duration} {t('common.min')}</span>
                  </Box>
                )}
                {isCompleted && (
                  <Box className="capsule__banner-tag">
                    <CheckIcon size={16} className="capsule__banner-tag-icon" />
                    <span>{t('common.completed')}</span>
                  </Box>
                )}
              </Box>
            </Box>
            <p className="capsule__banner-title">
              {getLocalizedText(capsule.title)}
            </p>
          </Box>
        </Box>
      ) : (
        <Box className="capsule__banner" sx={{ backgroundColor: '#2A2F2A' }}>
          <Box className="capsule__banner-overlay">
            <Box className="capsule__banner-top">
              <Typography className="capsule__banner-label">
                {capsuleBannerLabel}
              </Typography>
              <Box className="capsule__banner-tags">
                {duration !== undefined && duration > 0 && (
                  <Box className="capsule__banner-tag">
                    <TimeIcon size={16} className="capsule__banner-tag-icon" />
                    <span>{duration} {t('common.min')}</span>
                  </Box>
                )}
                {isCompleted && (
                  <Box className="capsule__banner-tag">
                    <CheckIcon size={16} className="capsule__banner-tag-icon" />
                    <span>{t('common.completed')}</span>
                  </Box>
                )}
              </Box>
            </Box>
            <p className="capsule__banner-title">
              {getLocalizedText(capsule.title)}
            </p>
          </Box>
        </Box>
      )}

      {/* Main Content */}
      <Box
        className="capsule__content"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
      >

        {/* Modules */}
        {sortedModules.length > 0 ? (
          sortedModules.map((module, index) => {
            return renderModule(module, index);
          })
        ) : (
          <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
            {t('capsule.noModules')}
          </Typography>
        )}

      </Box>
      {/* Footer Section */}
      <Box className="capsule__footer">
        <Box className="capsule__footer-header">
          <Typography className="capsule__footer-title">
            {t('capsule.footerTitle')}
          </Typography>
        </Box>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder={t('capsule.footerPlaceholder')}
          value={notes}
          onChange={(e) => {
            e.stopPropagation();
            setNotes(e.target.value);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="capsule__footer-textarea"
        />
        {course && courseId && chapterId && (
          <Button
            variant="contained"
            onClick={async () => {
              if (!chapterId || !courseId) return;
              const capsuleOrderNum = capsule?.order || parseInt(capsuleOrder || '0');

              // Mark capsule as viewed/completed when clicking continue button
              try {
                const capsuleItem = course.chapters
                  ?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId))
                  ?.items?.find((item: any) => {
                    const id = item?._id?.toString?.() || item?._id;
                    if (capsuleId) return item.itemType === 'capsule' && id === capsuleId;
                    return item.itemType === 'capsule' && item.order === capsuleOrderNum;
                  });
                const capsuleItemId = capsuleItem?._id?.toString();
                if (capsuleItemId) {
                  await progressAPI.markItemComplete(courseId, chapterId, 'capsule', capsuleItemId, capsuleOrderNum);
                  // Refresh course data to update item.isCompleted in timeline
                  await refreshCourse();
                  await refreshProgress();
                  await refreshAllProgress();
                }
              } catch (error: any) {
                console.error('❌ Error marking capsule as complete:', error?.response?.data || error?.message);
              }

              const capsuleItem = course.chapters
                ?.find((ch: any) => normalizeId(ch._id) === normalizeId(chapterId))
                ?.items?.find((item: any) => {
                  const id = item?._id?.toString?.() || item?._id;
                  if (capsuleId) return item.itemType === 'capsule' && id === capsuleId;
                  return item.itemType === 'capsule' && item.order === capsuleOrderNum;
                });
              const capsuleItemId = capsuleItem?._id?.toString() || '';

              const nextItem = getNextItem(course, chapterId, capsuleItemId);
              const nextPath = getNextItemPath(nextItem);
              if (nextPath) {
                navigate(nextPath);
              }
            }}
            className="button-primary capsule__footer-button"
          >
            {t('capsule.continueButton')}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default Capsule;

